import { isArray, isEmpty, isObject } from '@dimjs/lang';
import { classNames } from '@dimjs/utils';
import { toArray, TPlainObject } from '@flatbiz/utils';
import { hooks } from '@wove/react';
import { FC, ReactElement, useMemo, useState } from 'react';
import { Button } from '../button';
import { CheckList, CheckListItem, CheckListItemContentProps } from '../check-list';
import { Empty } from '../empty';
import { Form } from '../form';
import { SearchInputFormItem, SearchInputFormItemProps } from '../form-item';
import { useEffectCustom } from '../hooks';
import { CheckIcon } from '../icon';
import { IphoneBottomAdaptive } from '../iphone-bottom-adaptive';
import { ModalDrawer, ModalDrawerProps } from '../modal-drawer';
import { NativeScroll } from '../native-scroll';
import { CssActive } from '../simple-layout';
import { Space } from '../space';
import { px2rem } from '../utils/pxrem';
import './style.less';
import { TModalSelectorDataItem, TModalSelectorRespItem, TModalSelectorValue } from './types';
import { getModalSelectorRespData, parseInputValue } from './utils';

export interface ModalSelectorProps {
  visible?: boolean;
  className?: string;
  title: string | ReactElement;
  value?: TModalSelectorValue;
  multiple?: boolean;
  selectorList: TModalSelectorDataItem[];
  onClose?: () => void;
  onChange?: (value?: TModalSelectorRespItem) => void;
  /**
   * 弹框中是否添加选择确认按钮，默认值true
   */
  needSelectConfirm?: boolean;
  required?: boolean;
  onValueChange?: (data?: TModalSelectorDataItem | TModalSelectorDataItem[]) => void;
  labelAdaptive?: (dataItem: TModalSelectorDataItem) => ReactElement | string;
  checkItemAdaptive?: (checkItem: TModalSelectorDataItem & CheckListItemContentProps) => ReactElement;
  showSearch?: true | Omit<SearchInputFormItemProps, 'name' | 'onSearch'>;
  /**
   * 文案配置
   */
  copyWritingConfigs?: {
    selectorListIsEmpty?: string | ReactElement;
    searchListIsEmpty?: string | ReactElement;
    confirmButton?: string | ReactElement;
  };
  contentBeforeRender?: ReactElement;
  contentAfterRender?: ReactElement;
  /**
   * 选择器列表高度，默认值600，组件会对数值进行px2rem处理
   */
  selectorListHeight?: number;
  modalDrawerProps?: Omit<ModalDrawerProps, 'visible' | 'className' | 'title' | 'onClose'>;
}

/**
 * 弹框勾选组件
 * ```
 * 1. 与Form结合使用取值方式
 *    例如：
 *    b. 取值格式: { label: '白金会员', value: '1' }
 *       可通过使用form.getFormModalPickerValue(value) 直接取值 => '1'
 * ```
 */
export const ModalSelector: FC<ModalSelectorProps> = (props) => {
  const selectorListHeight = props.selectorListHeight || 600;
  const [keyword, setKeyword] = useState<string>();
  const forceUpdate = hooks.useForceUpdate();
  const [form] = Form.useForm();
  const selectorList = useMemo(() => {
    return props.selectorList || [];
  }, [props.selectorList]);

  const onValueChange = hooks.useCallbackRef((data?: TModalSelectorDataItem | TModalSelectorDataItem[]) => {
    const targetList = toArray<TModalSelectorDataItem>(data);
    if (props.multiple) {
      props.onValueChange?.(targetList);
    } else {
      props.onValueChange?.(targetList[0]);
    }
  });

  useEffectCustom(() => {
    if (!isEmpty(props.value)) {
      if (selectorList.length) {
        const values = parseInputValue(props.value, props.multiple);
        const targetList = selectorList.filter((item) => values.includes(item.value));
        form.setFieldsValue({ checkedValue: targetList.map((item) => item.value) });
        onValueChange(targetList);
        forceUpdate();
      }
    } else {
      form.setFieldsValue({ checkedValue: undefined });
      onValueChange(undefined);
    }
  }, [props.value, selectorList]);

  const onInnerChange = hooks.useCallbackRef((value: TModalSelectorValue) => {
    const target = getModalSelectorRespData(value, props.selectorList, props.multiple);
    if (isArray(target?.value)) {
      onValueChange(target?.value);
    } else {
      onValueChange(target as TModalSelectorDataItem);
    }
    props.onChange?.(target);
  });

  const onConfirm = hooks.useCallbackRef(() => {
    const values = form.getFieldsValue();
    onInnerChange(values?.['checkedValue'] as unknown as TModalSelectorValue);
  });

  const onSearch = hooks.useCallbackRef(() => {
    setKeyword(form.getFieldValue('search') as string);
  });

  const renderList = useMemo(() => {
    if (!keyword) return selectorList;
    return selectorList.filter((item) => {
      return item.label.includes(keyword);
    });
  }, [selectorList, keyword]);

  const searchInputFormItemProps = isObject(props.showSearch) ? (props.showSearch as TPlainObject) : {};

  const onItemValueChange = hooks.useCallbackRef((value) => {
    if (!props.needSelectConfirm) {
      setTimeout(() => {
        onInnerChange(value as TModalSelectorValue);
      }, 100);
    }
  });

  return (
    <ModalDrawer
      closeIcon={true}
      {...props.modalDrawerProps}
      className={classNames('v-modal-selector', props.className)}
      visible={props.visible}
      onClose={props.onClose}
      title={props.title}
    >
      <Form form={form}>
        {props.contentBeforeRender}
        {props.showSearch ? (
          <div className="v-modal-selector-search">
            <SearchInputFormItem {...searchInputFormItemProps} name="search" onSearch={onSearch} />
          </div>
        ) : null}
        <NativeScroll height={px2rem(selectorListHeight)}>
          <Form.Item isCustom name="checkedValue" onItemValueChange={onItemValueChange}>
            <CheckList
              multiple={props.multiple}
              required={props.required}
              checkedClassPrefix="v-modal-selector"
              className="v-modal-selector-check-list"
            >
              {renderList.map((item, index) => {
                return (
                  <CheckListItem key={index} value={item.value}>
                    {(data) => {
                      return props.checkItemAdaptive ? (
                        props.checkItemAdaptive({ ...data, label: item.label, value: item.value })
                      ) : (
                        <CssActive>
                          <Space
                            onClick={data.onClick}
                            className={classNames('v-modal-selector-check-item', data.className)}
                          >
                            <span>{props.labelAdaptive ? props.labelAdaptive(item) : item.label}</span>
                            <span>
                              <CheckIcon checked={data.checked} />
                            </span>
                          </Space>
                        </CssActive>
                      );
                    }}
                  </CheckListItem>
                );
              })}
            </CheckList>
          </Form.Item>
          <Empty
            isShow={!!keyword && renderList.length === 0 && selectorList.length > 0}
            text={props.copyWritingConfigs?.searchListIsEmpty}
          />
          <Empty
            isShow={selectorList.length === 0}
            text={props.copyWritingConfigs?.selectorListIsEmpty || ''}
          />
        </NativeScroll>
        {props.needSelectConfirm && selectorList.length > 0 ? (
          <div className="v-modal-selector-operate">
            <Button type="primary" onClick={onConfirm}>
              {props.copyWritingConfigs?.confirmButton}
            </Button>
            <IphoneBottomAdaptive />
          </div>
        ) : null}
        {props.contentAfterRender}
      </Form>
    </ModalDrawer>
  );
};

ModalSelector.defaultProps = {
  needSelectConfirm: true,
  copyWritingConfigs: {
    selectorListIsEmpty: '暂无数据',
    searchListIsEmpty: '搜索结果无数据',
    confirmButton: '确认',
  },
};
